import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Radar,
  Radio,
  Activity,
  Zap,
  Shield,
  Target,
  Send,
  AlertTriangle,
  CheckCircle,
  Clock,
  Terminal,
  Cpu,
  Loader2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Radar Animation Component
const RadarSweep = ({ agents }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) - 20;
    
    let angle = 0;

    const draw = () => {
      ctx.fillStyle = 'rgba(0, 10, 0, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.strokeStyle = 'rgba(0, 255, 65, 0.2)';
      ctx.lineWidth = 1;
      for (let i = 1; i <= 4; i++) {
        ctx.beginPath();
        ctx.arc(centerX, centerY, (radius / 4) * i, 0, Math.PI * 2);
        ctx.stroke();
      }

      ctx.beginPath();
      ctx.moveTo(centerX - radius, centerY);
      ctx.lineTo(centerX + radius, centerY);
      ctx.moveTo(centerX, centerY - radius);
      ctx.lineTo(centerX, centerY + radius);
      ctx.stroke();

      // Draw sweep line
      ctx.strokeStyle = '#00FF41';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + Math.cos(angle) * radius,
        centerY + Math.sin(angle) * radius
      );
      ctx.stroke();

      // Draw sweep fill
      ctx.fillStyle = 'rgba(0, 255, 65, 0.05)';
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, angle - 0.5, angle);
      ctx.closePath();
      ctx.fill();

      // Draw agent blips
      agents.forEach((agent, i) => {
        const agentAngle = (i / Math.max(agents.length, 1)) * Math.PI * 2;
        const agentRadius = radius * (0.3 + (i % 3) * 0.2);
        const x = centerX + Math.cos(agentAngle) * agentRadius;
        const y = centerY + Math.sin(agentAngle) * agentRadius;

        const color = agent.status === 'active' ? '#00FF41' : 
          agent.status === 'paused' ? '#FFB800' : '#FF003C';
        
        ctx.fillStyle = color;
        ctx.shadowColor = color;
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      // Center dot
      ctx.fillStyle = '#00FF41';
      ctx.beginPath();
      ctx.arc(centerX, centerY, 5, 0, Math.PI * 2);
      ctx.fill();

      angle += 0.02;
      if (angle > Math.PI * 2) angle = 0;
    };

    const interval = setInterval(draw, 30);
    return () => clearInterval(interval);
  }, [agents]);

  return (
    <canvas
      ref={canvasRef}
      width={300}
      height={300}
      className="rounded"
    />
  );
};

// OpenClaw Status Indicator
const OpenClawStatus = ({ status }) => {
  const statusColors = {
    online: { bg: 'bg-primary/20', text: 'text-primary', dot: 'bg-primary' },
    degraded: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', dot: 'bg-yellow-400' },
    offline: { bg: 'bg-red-500/20', text: 'text-red-400', dot: 'bg-red-400' },
    checking: { bg: 'bg-gray-500/20', text: 'text-gray-400', dot: 'bg-gray-400' }
  };

  const colors = statusColors[status] || statusColors.checking;

  return (
    <div className={`flex items-center gap-2 px-3 py-1 rounded ${colors.bg}`}>
      <span className={`w-2 h-2 rounded-full ${colors.dot} ${status === 'online' ? 'animate-pulse' : ''}`} />
      <span className={`text-xs font-mono uppercase tracking-wider ${colors.text}`}>
        {status}
      </span>
    </div>
  );
};

// Mission Control Component
const MissionControl = ({ agents, stats }) => {
  const [openclawStatus, setOpenclawStatus] = useState({ status: 'checking' });
  const [taskInput, setTaskInput] = useState('');
  const [taskResult, setTaskResult] = useState(null);
  const [taskLoading, setTaskLoading] = useState(false);
  const [recentTasks, setRecentTasks] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/openclaw/status`);
        setOpenclawStatus(response.data);
      } catch (error) {
        setOpenclawStatus({ status: 'offline', error: error.message });
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const response = await axios.get(`${API}/openclaw/tasks`, { withCredentials: true });
        setRecentTasks(response.data.slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch tasks:', error);
      }
    };
    fetchTasks();
  }, [taskResult]);

  const executeTask = async () => {
    if (!taskInput.trim() || taskLoading) return;
    
    setTaskLoading(true);
    setTaskResult(null);
    
    try {
      const response = await axios.post(
        `${API}/openclaw/execute`,
        { task: taskInput },
        { withCredentials: true }
      );
      setTaskResult(response.data);
      setTaskInput('');
    } catch (error) {
      setTaskResult({ 
        status: 'error', 
        result: error.response?.data?.detail || error.message 
      });
    } finally {
      setTaskLoading(false);
    }
  };

  const sendChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMsg = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/chat`,
        { message: chatInput, provider: process.env.REACT_APP_MISSION_PROVIDER || 'router' },
        { withCredentials: true }
      );
      const aiMsg = { role: 'assistant', content: response.data.response, model: response.data.model };
      setChatMessages(prev => [...prev, aiMsg]);
    } catch (error) {
      const errMsg = { role: 'error', content: error.response?.data?.detail || error.message };
      setChatMessages(prev => [...prev, errMsg]);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-military text-2xl text-primary flex items-center gap-3">
            <Radar className="w-7 h-7" />
            MISSION CONTROL
          </h2>
          <p className="text-primary/50 font-mono text-sm mt-1">OpenClaw Neural Link Active</p>
        </div>
        <OpenClawStatus status={openclawStatus.status} />
      </div>

      {/* Main Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Radar Panel */}
        <div className="cyber-card p-6 border border-primary/20">
          <h3 className="font-orbitron text-sm font-bold mb-4 flex items-center gap-2 text-primary">
            <Target className="w-4 h-4" />
            AGENT TRACKING
          </h3>
          <div className="flex justify-center">
            <RadarSweep agents={agents} />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-2xl font-orbitron text-primary">{stats.active_agents}</p>
              <p className="text-[10px] font-mono text-primary/50">ACTIVE</p>
            </div>
            <div>
              <p className="text-2xl font-orbitron text-yellow-400">{stats.total_agents - stats.active_agents}</p>
              <p className="text-[10px] font-mono text-primary/50">STANDBY</p>
            </div>
            <div>
              <p className="text-2xl font-orbitron text-primary">{stats.tasks_completed}</p>
              <p className="text-[10px] font-mono text-primary/50">OPS</p>
            </div>
          </div>
        </div>

        {/* OpenClaw Chat */}
        <div className="cyber-card p-6 border border-primary/20 flex flex-col">
          <h3 className="font-orbitron text-sm font-bold mb-4 flex items-center gap-2 text-primary">
            <Terminal className="w-4 h-4" />
            OPENCLAW NEURAL LINK
          </h3>
          
          <div className="flex-1 min-h-[200px] max-h-[300px] overflow-y-auto space-y-3 mb-4" data-testid="openclaw-chat">
            {chatMessages.length === 0 && (
              <div className="text-center py-8 text-primary/30">
                <Cpu className="w-12 h-12 mx-auto mb-2" />
                <p className="font-mono text-xs">NEURAL LINK READY</p>
              </div>
            )}
            {chatMessages.map((msg, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-3 rounded font-mono text-sm ${
                  msg.role === 'user' 
                    ? 'bg-primary/10 text-primary ml-8' 
                    : msg.role === 'error'
                    ? 'bg-red-500/10 text-red-400 mr-8'
                    : 'bg-black/50 text-gray-300 mr-8 border border-primary/10'
                }`}
              >
                {msg.role === 'assistant' && (
                  <p className="text-[10px] text-primary/50 mb-1">OPENCLAW:MAIN</p>
                )}
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </motion.div>
            ))}
            {chatLoading && (
              <div className="flex items-center gap-2 text-primary/50 p-3">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-xs font-mono">PROCESSING...</span>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendChat()}
              placeholder="Query OpenClaw..."
              className="cyber-input flex-1 text-sm py-2"
              data-testid="openclaw-chat-input"
            />
            <button
              onClick={sendChat}
              disabled={chatLoading || !chatInput.trim()}
              className="cyber-btn px-4"
              data-testid="openclaw-chat-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Task Execution */}
        <div className="cyber-card p-6 border border-primary/20">
          <h3 className="font-orbitron text-sm font-bold mb-4 flex items-center gap-2 text-primary">
            <Zap className="w-4 h-4" />
            TASK EXECUTION
          </h3>
          
          <div className="space-y-4">
            <textarea
              value={taskInput}
              onChange={(e) => setTaskInput(e.target.value)}
              placeholder="Enter autonomous task for OpenClaw..."
              className="cyber-input w-full h-24 resize-none text-sm"
              data-testid="openclaw-task-input"
            />
            <button
              onClick={executeTask}
              disabled={taskLoading || !taskInput.trim()}
              className="cyber-btn w-full flex items-center justify-center gap-2"
              data-testid="openclaw-task-execute"
            >
              {taskLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  EXECUTING...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  EXECUTE TASK
                </>
              )}
            </button>
          </div>

          <AnimatePresence>
            {taskResult && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className={`mt-4 p-3 rounded border ${
                  taskResult.status === 'completed' 
                    ? 'bg-primary/5 border-primary/30' 
                    : 'bg-red-500/5 border-red-500/30'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {taskResult.status === 'completed' ? (
                    <CheckCircle className="w-4 h-4 text-primary" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                  )}
                  <span className="text-xs font-mono uppercase">
                    {taskResult.status === 'completed' ? 'TASK COMPLETE' : 'EXECUTION ERROR'}
                  </span>
                </div>
                <p className="text-xs font-mono text-gray-400 max-h-32 overflow-y-auto whitespace-pre-wrap">
                  {taskResult.result}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {recentTasks.length > 0 && (
            <div className="mt-4 pt-4 border-t border-primary/10">
              <p className="text-[10px] font-mono text-primary/50 mb-2">RECENT OPERATIONS</p>
              <div className="space-y-2">
                {recentTasks.map((task, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs font-mono">
                    <Clock className="w-3 h-3 text-primary/30" />
                    <span className="text-gray-500 truncate flex-1">{task.task}</span>
                    <span className={task.status === 'completed' ? 'text-primary' : 'text-red-400'}>
                      {task.status === 'completed' ? '✓' : '✗'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* System Status Bar */}
      <div className="cyber-card p-4 border border-primary/20">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-primary" />
              <span className="text-xs font-mono text-primary/70">NEURAL LINK</span>
              <span className={`text-xs font-mono ${openclawStatus.status === 'online' ? 'text-primary' : 'text-red-400'}`}>
                {openclawStatus.status === 'online' ? 'CONNECTED' : 'DISCONNECTED'}
              </span>
            </div>
            {openclawStatus.latency_ms && (
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-primary" />
                <span className="text-xs font-mono text-primary/70">LATENCY</span>
                <span className="text-xs font-mono text-primary">{Math.round(openclawStatus.latency_ms)}ms</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" />
            <span className="text-xs font-mono text-primary/50">ENCRYPTION: AES-256 // CHANNEL: SECURE</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionControl;
